import { useQuery } from '@tanstack/react-query';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, subDays, startOfDay, isSameDay, isToday } from 'date-fns';
import { useState } from 'react';
import { fetchDailySummaries } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { useLanguage } from '@/core/LanguageContext';
import { Button } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Calendar } from '@/shared/ui/calendar';
import { cn } from '@/shared/lib/utils';

interface ScrapDatePickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

/**
 * Compact gamified date picker - shows last 5 days inline with today's status.
 */
export const ScrapDatePicker = ({ selectedDate, onDateChange }: ScrapDatePickerProps) => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { t } = useLanguage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const endDate = format(new Date(), 'yyyy-MM-dd');

  const { data: summaries = [] } = useQuery({
    queryKey: ['scrap-daily-summaries', selectedTenantId, selectedFactoryId, startDate, endDate],
    queryFn: () => {
      if (!selectedFactoryId) return [];
      return fetchDailySummaries(selectedFactoryId, startDate, endDate);
    },
    enabled: !!selectedFactoryId,
  });

  const hasEntriesMap = new Map(summaries.map((s) => [s.entry_date, true]));
  const today = startOfDay(new Date());
  const todayStr = format(today, 'yyyy-MM-dd');

  // Last 5 days including today
  const recentDays = Array.from({ length: 5 }, (_, i) => subDays(today, 4 - i));

  const getStatusStyles = (date: Date, isSelected: boolean) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const hasEntries = hasEntriesMap.has(dateStr);
    const isDateToday = isToday(date);
    const isPast = dateStr < todayStr;

    const base = 'w-9 h-9 rounded-md text-xs font-medium transition-all flex flex-col items-center justify-center';
    
    if (isSelected) {
      return cn(base, 'ring-2 ring-primary ring-offset-1');
    }
    
    if (hasEntries) {
      return cn(base, 'bg-green-500/20 text-green-700 dark:text-green-400');
    }
    if (isDateToday) {
      return cn(base, 'bg-amber-500/20 text-amber-700 dark:text-amber-400');
    }
    if (isPast) {
      return cn(base, 'bg-red-500/20 text-red-700 dark:text-red-400');
    }
    return cn(base, 'bg-muted text-muted-foreground');
  };

  const modifiers = {
    hasEntries: (date: Date) => hasEntriesMap.has(format(date, 'yyyy-MM-dd')),
    missingEntries: (date: Date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return dateStr < todayStr && !hasEntriesMap.has(dateStr);
    },
  };

  const modifiersClassNames = {
    hasEntries: 'bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100 font-semibold',
    missingEntries: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
  };

  return (
    <div className="flex items-center gap-1">
      {recentDays.map((date) => {
        const isSelected = isSameDay(date, selectedDate);
        const isDateToday = isToday(date);
        
        return (
          <button
            key={date.toISOString()}
            onClick={() => onDateChange(date)}
            className={getStatusStyles(date, isSelected)}
            title={format(date, 'EEEE, MMMM d')}
          >
            <span className="text-[10px] leading-none opacity-70">
              {isDateToday ? 'Today' : format(date, 'EEE')}
            </span>
            <span className="font-bold leading-none">{format(date, 'd')}</span>
          </button>
        );
      })}

      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-9 w-9" title="View calendar">
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              if (date) {
                onDateChange(date);
                setIsCalendarOpen(false);
              }
            }}
            initialFocus
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            disabled={(date) => date > new Date()}
          />
          <div className="px-3 py-2 border-t flex gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" /> {t('core.scrap.done', 'Done')}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> {t('core.scrap.pending', 'Pending')}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" /> {t('core.scrap.missing', 'Missing')}
            </span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
